// iter59 · Slice 2A — Set Up Your Business dashboard card.
// Single contractor-facing setup card. Consumes the new state endpoint
// (/api/onboarding/set-up-your-business/state), replaces the legacy
// "Quick Start" checklist on the Dashboard. Renders nothing ONLY once
// EVERY ONE of the four explicit approved onboarding steps has been
// marked complete by its own approved slice — never based on aggregate
// counters, legacy ``setup_completed`` flags, or Pricing Settings
// readiness. Until Slices 2B / 2C / 2D ship the remaining three steps,
// the card must remain visible after Step 1 completion.
//
// The card intentionally does NOT include destructive "dismiss" or
// "skip" affordances — Set Up Your Business is a persistent home for
// business identity + trades and should always be reachable via the
// "Continue Setup" / "Review Setup" CTA.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, CheckCircle2, ArrowUpRight } from "lucide-react";
import { resolveSetupDestination } from "@/lib/setupProgress";

// Canonical order of the four approved onboarding steps. Slices 2B-2D
// will mark the remaining three ``complete: true`` once shipped. Until
// then, they stay ``false`` at the backend and this card stays visible.
const APPROVED_STEP_KEYS = [
  "business_and_trades",
  "trade_setup",
  "documents_and_customer_defaults",
  "review_and_ready",
];

const STEP_LABELS = {
  business_and_trades: "Business & Trades",
  trade_setup: "Trade Setup",
  documents_and_customer_defaults: "Documents & Customer Defaults",
  review_and_ready: "Review & Ready",
};

const SetUpYourBusinessCard = () => {
  const [state, setState] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/onboarding/set-up-your-business/state"),
      api.get("/company-profile"),
    ])
      .then(([sR, pR]) => {
        if (cancelled) return;
        setState(sR.data);
        setProfile(pR.data);
      })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, []);

  // iter59 · Slice 2A fix — network error / stale cache must NEVER
  // silently hide the card. Fall back to the "Business & Trades needs
  // attention" state so contractors always have a visible path back to
  // /app/setup. Only a successful state response can hide the card,
  // and only when every approved step is explicitly complete.
  const stepsByKey = {};
  for (const s of (state?.steps || [])) stepsByKey[s.key] = s;
  const step1Complete = !!stepsByKey.business_and_trades?.complete;
  // Every approved step must report ``complete: true`` before we hide.
  // Any missing key is treated as incomplete (defensive).
  const allApprovedStepsComplete = APPROVED_STEP_KEYS.every(
    (k) => stepsByKey[k]?.complete === true,
  );
  if (state && allApprovedStepsComplete) return null;

  const completedCount = APPROVED_STEP_KEYS.reduce(
    (n, k) => n + (stepsByKey[k]?.complete === true ? 1 : 0),
    0,
  );
  const totalCount = APPROVED_STEP_KEYS.length;
  // Next step = first approved step that isn't yet complete.
  const nextStepKey =
    APPROVED_STEP_KEYS.find((k) => !stepsByKey[k]?.complete)
    || "business_and_trades";

  // Slice 2B1 UX polish — CTA wording:
  //   • Any incomplete approved step  → "Continue Setup"
  //   • Every approved step complete  → "Review Setup" (only reachable
  //     when the top-level ``allApprovedStepsComplete`` gate above did
  //     not hide the card — kept for a future "peek" mode).
  const ctaLabel = allApprovedStepsComplete ? "Review Setup" : "Continue Setup";
  const bannerLine = step1Complete
    ? `Next: ${STEP_LABELS[nextStepKey]}`
    : "Business & Trades needs attention";

  // Slice 2B1 · Canonical resolver — the CTA MUST land the
  // contractor on the correct next step (not the bare `/app/setup`
  // welcome fallback which loops back to Business & Trades when
  // it is already complete). Falls back to `/app/setup` while the
  // profile/state is still loading.
  const dest = state && profile
    ? resolveSetupDestination(state, profile)
    : { path: "/app/setup" };

  // While the state endpoint is still loading (no state yet AND no error),
  // avoid a flash of the wrong content — render the pre-Step-1 fallback.
  // On error, we do the same. This guarantees the card is never hidden
  // by a transient failure.
  const showLoading = !state && !loadFailed;

  return (
    <section
      data-testid="soyb-card"
      data-loaded={state ? "true" : "false"}
      data-load-failed={loadFailed ? "true" : "false"}
      className="jc-card p-4 mb-6 flex items-center justify-between gap-3 border-l-4 border-l-[var(--jc-blue)]"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="shrink-0 w-9 h-9 rounded-md bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] flex items-center justify-center">
          {step1Complete ? <CheckCircle2 size={18} /> : <Sparkles size={18} />}
        </div>
        <div className="min-w-0">
          <div
            className="text-sm font-semibold text-[var(--jc-text)]"
            data-testid="soyb-card-title"
          >
            Set Up Your Business
          </div>
          <div className="text-xs text-[var(--jc-text-soft)]">
            <span data-testid="soyb-card-progress">
              {showLoading ? "Loading progress…" : `${completedCount} of ${totalCount} steps complete`}
            </span>
            {!showLoading && (
              <>
                <span className="mx-1">·</span>
                <span data-testid="soyb-card-next">{bannerLine}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <Link
        to={dest.path}
        data-testid="soyb-card-cta"
        data-resolved-step={dest.step || ""}
        data-resolved-trade={dest.trade || ""}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-primary"
      >
        {ctaLabel} <ArrowUpRight size={12} />
      </Link>
    </section>
  );
};

export default SetUpYourBusinessCard;
